import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusCircle, BookOpen, GraduationCap, Target, Sparkles, Upload, LogOut, ListTree, Users, BarChart3, FileBarChart,
  UserCog, Shield, UserCheck, Users2, LifeBuoy, Layers, CalendarClock, ToggleLeft, CreditCard, ChevronDown,
  FolderCog, BookOpenCheck, KeyRound, Archive, SlidersHorizontal, MessageSquare,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useAdminAccess from '../../hooks/useAdminAccess';

// Shared left sidebar for the whole admin console - used by AdminPortal.jsx,
// AdminSettings.jsx, AdminStudents.jsx, MentorWorkspace.jsx,
// ManagerDashboard.jsx, and SupportDesk.jsx, so this ONE component is every
// employee role's (Admin/Manager/Mentor/Support) navigation (docs/
// DECISIONS.md, "Admin console navigation redesign" - direct user request,
// "Grouping the related sections into menus and sub menus... 10x better").
//
// Collapsible accordion groups, not a flat always-expanded list (the prior
// design - confirmed via AskUserQuestion this was the wanted direction over
// just more flat labeled sections): with every group a role has access to
// expanded at once, a superadmin saw ~20 nav buttons stacked simultaneously
// before this. Each group starts expanded only if it contains the current
// page (`activeKey`); manual expand/collapse choices persist across
// navigations via localStorage, since this component remounts fresh on
// every page navigation (each page constructs its own `<AdminSidebar
// activeKey="..."/>` - confirmed by reading every caller) - without
// persistence, expanding a group would silently re-collapse the instant a
// user clicked one of its own items.
const MASTER_DATA_ITEMS = [
  { key: 'subjects', label: 'Subjects', icon: BookOpen },
  { key: 'grades', label: 'Grades', icon: GraduationCap },
  { key: 'target-exams', label: 'Target Exams', icon: Target },
];

const CURRICULUM_ITEMS = [
  { key: 'syllabus', label: 'Syllabus', icon: ListTree },
  { key: 'concept-hub', label: 'Concept Hub', icon: Layers },
];

// Feature Visibility/Subscription Plans (docs/DECISIONS.md, "Admin-
// controlled feature visibility per class" plan file) - grouped together as
// the two newest, most closely-related additions (student-facing gating/
// entitlement), rather than left in the old flat Settings block.
const ACCESS_ITEMS = [
  { key: 'feature-visibility', label: 'Feature Visibility', icon: ToggleLeft },
  { key: 'subscription-plans', label: 'Subscription Plans', icon: CreditCard },
  // Admin-editable System Configuration (docs/DECISIONS.md, "Admin-editable
  // System Configuration" plan file) - business-tuning config values,
  // grouped here alongside the other entitlement/access-adjacent settings
  // rather than a new top-level group.
  { key: 'system-config', label: 'System Configuration', icon: SlidersHorizontal },
];

// Generate Question Prompt/Upload Questions moved in here to join Practice/
// Exam Question Stock - all four are question-bank-related, so this avoids
// a 4th new group for just 2 items.
const QUESTION_BANK_ITEMS = [
  { key: 'practice-stock', label: 'Practice Question Stock', icon: BarChart3 },
  { key: 'exam-stock', label: 'Exam Question Stock', icon: FileBarChart },
  { key: 'question-prompt', label: 'Generate Question Prompt', icon: Sparkles },
  { key: 'upload-questions', label: 'Upload Questions', icon: Upload },
];

// RUDVAN Admin Module (docs/DECISIONS.md, "RUDVAN Admin Module: internal
// staff, RBAC, student-mentor allocation") - visible to everyone who
// reaches /admin (backend + the pages themselves gate the actual data/
// actions per-permission, FR-ADM-10's "hide, don't just reject" is handled
// inside AdminStaff.jsx/AdminRoles.jsx once access is resolved, not by
// hiding the nav entry itself here).
const TEAM_ITEMS = [
  { key: 'staff', label: 'Staff', icon: UserCog },
  { key: 'roles', label: 'Roles & Permissions', icon: Shield },
];

// Mentor/Manager/Support Capabilities (US-MEN-*/US-MGR-*/US-SUP-*, docs/
// DECISIONS.md, "RUDVAN Admin Module..." Phase 4-6) - hidden per-item (not
// just gated inside the page): these are workspaces most Admins/other roles
// never use, so cluttering every admin's sidebar with "My Students"/"My
// Team"/"Support Tickets" regardless of role would be noisier than useful.
// `permission` is checked against GET /api/admin/me via useAdminAccess below.
const MY_WORK_ITEMS = [
  { key: 'mentor', label: 'My Students', icon: UserCheck, permission: 'student_profile:view' },
  { key: 'manager', label: 'My Team', icon: Users2, permission: 'staff:view' },
  { key: 'support', label: 'Support Tickets', icon: LifeBuoy, permission: 'support:view' },
  // Admin/Manager availability edit access (docs/DECISIONS.md, 2026-08-27
  // "Admin/Manager availability edit access") - reachable by superadmin
  // (via the `access?.is_superadmin` bypass below) or anyone explicitly
  // granted student_availability:edit_any via the Roles UI.
  { key: 'student-availability', label: 'Student Availability', icon: CalendarClock, permission: 'student_availability:edit_any' },
];

// LEXA analytics dashboard (docs/DECISIONS.md, 2026-08-28 "LEXA -
// Phase 5") - same hidden-per-item treatment as MY_WORK_ITEMS above (not
// everyone with admin access should see a platform-wide AI-usage dashboard
// cluttering their sidebar).
const INSIGHTS_ITEMS = [
  { key: 'lexa-analytics', label: 'LEXA Analytics', icon: Sparkles, permission: 'lexa_analytics:view' },
  // Pilot user feedback (docs/DECISIONS.md, "New feature approved: User
  // Feedback") - same single-permission, not-defaulted-to-any-role,
  // read-only dashboard-style treatment as LEXA Analytics above.
  { key: 'feedback', label: 'User Feedback', icon: MessageSquare, permission: 'feedback:view' },
];

// One shared definition per group - the single source both the accordion
// UI and the "which group contains the active page" lookup below iterate,
// so the two can never drift out of sync with each other.
const GROUPS = [
  { id: 'team', label: 'Team', icon: UserCog, items: TEAM_ITEMS },
  { id: 'my-work', label: 'My Work', icon: Users2, items: MY_WORK_ITEMS, permissionGated: true },
  { id: 'insights', label: 'Insights', icon: Sparkles, items: INSIGHTS_ITEMS, permissionGated: true },
  { id: 'question-bank', label: 'Question Bank', icon: Archive, items: QUESTION_BANK_ITEMS },
  { id: 'master-data', label: 'Master Data', icon: FolderCog, items: MASTER_DATA_ITEMS },
  { id: 'curriculum', label: 'Curriculum', icon: BookOpenCheck, items: CURRICULUM_ITEMS },
  { id: 'access', label: 'Access & Plans', icon: KeyRound, items: ACCESS_ITEMS },
];

const EXPANDED_GROUPS_STORAGE_KEY = 'adminSidebarExpandedGroups';

function loadStoredExpandedGroups() {
  try {
    const raw = window.localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function saveStoredExpandedGroups(groupIds) {
  try {
    window.localStorage.setItem(EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify(groupIds));
  } catch (error) {
    // Private-browsing/storage-blocked - expand state just won't persist
    // across navigations this session, not worth surfacing as an error.
  }
}

function groupIdForActiveKey(activeKey) {
  const group = GROUPS.find((g) => g.items.some((item) => item.key === activeKey));
  return group ? group.id : null;
}

export default function AdminSidebar({ activeKey }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { access, can } = useAdminAccess();

  const activeGroupId = groupIdForActiveKey(activeKey);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const stored = new Set(loadStoredExpandedGroups());
    if (activeGroupId) stored.add(activeGroupId);
    return stored;
  });

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      saveStoredExpandedGroups([...next]);
      return next;
    });
  };

  const handleLogout = () => {
    if (!window.confirm('Do you really want to logout?')) return;
    logout();
    navigate('/login');
  };

  return (
    <nav className="md:w-64 flex-shrink-0">
      <h2 className="px-1 mb-3 text-xs font-bold uppercase tracking-wide text-text-slate hidden md:block">
        Menu
      </h2>
      <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
        <SidebarButton
          active={activeKey === 'configure'}
          icon={PlusCircle}
          label="Configure Exam"
          onClick={() => navigate('/admin')}
        />
        <SidebarButton
          active={activeKey === 'students'}
          icon={Users}
          label="Students"
          onClick={() => navigate('/admin/students')}
        />

        {GROUPS.map((group) => {
          const visibleItems = group.permissionGated
            ? group.items.filter((item) => access?.is_superadmin || can(item.permission))
            : group.items;
          if (visibleItems.length === 0) return null;

          const isSettingsGroup = ['master-data', 'curriculum', 'access', 'question-bank'].includes(group.id);
          const path = isSettingsGroup
            ? (key) => `/admin/settings?section=${key}`
            : (key) => `/admin/${key}`;

          return (
            <SidebarGroup
              key={group.id}
              group={group}
              items={visibleItems}
              activeKey={activeKey}
              expanded={expandedGroups.has(group.id)}
              onToggle={() => toggleGroup(group.id)}
              onNavigate={(key) => navigate(path(key))}
            />
          );
        })}

        <span className="hidden md:block h-px bg-gray-200 my-1" />

        <SidebarButton icon={LogOut} label="Logout" danger onClick={handleLogout} />
      </div>
    </nav>
  );
}

function SidebarGroup({ group, items, activeKey, expanded, onToggle, onNavigate }) {
  const GroupIcon = group.icon;
  const hasActiveItem = items.some((item) => item.key === activeKey);
  return (
    <div className="md:border md:border-gray-200 md:rounded-btn md:overflow-hidden flex-shrink-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold transition-colors ${
          hasActiveItem ? 'bg-brand-blue/5 text-navy' : 'bg-surface-light text-text-slate hover:bg-gray-100'
        }`}
      >
        <GroupIcon size={16} className={hasActiveItem ? 'text-brand-blue' : 'text-text-slate'} />
        <span className="flex-1 text-left whitespace-nowrap md:whitespace-normal">{group.label}</span>
        <ChevronDown size={15} className={`flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="md:p-1.5 md:pt-0 flex md:flex-col gap-1.5 md:gap-1">
          {items.map((item) => (
            <SidebarButton
              key={item.key}
              active={activeKey === item.key}
              icon={item.icon}
              label={item.label}
              onClick={() => onNavigate(item.key)}
              nested
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarButton({ active, icon: Icon, label, onClick, danger, nested }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-btn text-sm font-medium whitespace-nowrap transition-colors border text-left ${
        nested ? 'md:ml-1' : ''
      } ${
        danger
          ? 'bg-surface-light border-gray-200 text-red-600 hover:bg-red-50 hover:border-red-200'
          : active
            ? 'bg-brand-blue/10 border-brand-blue/40 text-navy'
            : 'bg-surface-light border-gray-200 text-text-slate hover:bg-gray-100 hover:border-gray-300'
      }`}
    >
      <Icon size={16} className={danger ? 'text-red-500' : active ? 'text-brand-blue' : 'text-text-slate'} />
      {label}
    </button>
  );
}
